import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, Pencil, X } from 'lucide-react'
import Link from '@/routing/Link'
import { KnowledgeDocumentFacet } from '@digital-twins/shared-types'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Text } from '@/components/ui/Text'
import {
  displayKnowledgeTypeLabel,
  facetConfidenceLabel,
  facetValues,
  knowledgeFacetKindLabel,
  marketDisplayName,
} from '../KnowledgeHubPage/libraryHelpers'

function pillLabel(kind: string, value: string): string {
  if (kind === 'market') {
    return marketDisplayName(value)
  }
  return kind === 'type' ? displayKnowledgeTypeLabel(value) : value
}

export interface FacetPill {
  facet: KnowledgeDocumentFacet
  value: string
}

export interface FacetGroup {
  kind: string
  label: string
  pills: Array<FacetPill>
}

/**
 * One group per facet kind, in the order the kinds first appear. Multi-value
 * kinds (a document tagged Italy *and* France, or four methodologies) get one
 * pill per value rather than a single collapsed chip.
 */
export function groupFacetsByKind(
  facets: Array<KnowledgeDocumentFacet>,
): Array<FacetGroup> {
  const kinds: Array<string> = []
  for (const facet of facets) {
    if (facet.rejected) {
      continue
    }
    if (!facet.value?.trim()) {
      continue
    }
    if (!kinds.includes(facet.kind)) {
      kinds.push(facet.kind)
    }
  }

  return kinds.map((kind) => {
    // `facetValues` decides which values survive (non-rejected, non-empty,
    // trimmed); the assignment list is filtered identically so the two arrays
    // stay index-aligned and every pill keeps its own assignment token.
    const values = facetValues(facets, kind)
    const assignments = facets.filter(
      (facet) =>
        facet.kind === kind && !facet.rejected && Boolean(facet.value?.trim()),
    )
    return {
      kind,
      label: assignments[0]?.displayName || kind,
      pills: assignments.map((facet, index) => ({
        facet,
        value: values[index] ?? facet.value,
      })),
    }
  })
}

export interface TagsCardProps {
  facets: Array<KnowledgeDocumentFacet>
  ownerName?: string | null
  busy: boolean
  onConfirmFacet: (facet: KnowledgeDocumentFacet) => void
  onRejectFacet: (facet: KnowledgeDocumentFacet) => void
  onCorrectFacet: (facet: KnowledgeDocumentFacet, value: string) => void
  onConfirmAllFacets: () => void
  /** False where the Hub's filtered library is not reachable (the back office). */
  linkToLibrary?: boolean
  /** Confirmed tags can be corrected or removed too (the back office). */
  editConfirmed?: boolean
}

export function TagsCard(props: TagsCardProps) {
  const {
    facets,
    ownerName,
    busy,
    onConfirmFacet,
    onRejectFacet,
    onCorrectFacet,
    onConfirmAllFacets,
    linkToLibrary = true,
    editConfirmed = false,
  } = props
  const { t } = useTranslation('app')

  const [editingToken, setEditingToken] = useState<string | null>(null)
  const [editValue, setEditValue] = useState('')

  const groups = useMemo(() => groupFacetsByKind(facets), [facets])
  const hasUnconfirmed = useMemo(
    () => facets.some((facet) => !facet.rejected && !facet.confirmed),
    [facets],
  )

  function startEditing(facet: KnowledgeDocumentFacet, value: string) {
    setEditingToken(facet.assignmentToken)
    setEditValue(value)
  }

  function saveEdit(facet: KnowledgeDocumentFacet) {
    const next = editValue.trim()
    if (!next) {
      return
    }
    onCorrectFacet(facet, next)
    setEditingToken(null)
  }

  const emptyState =
    groups.length === 0 && !ownerName ? (
      <Text size="sm" color="muted" data-testid="knowledge-doc-tags-empty">
        {t('library.preview.noTags')}
      </Text>
    ) : null

  return (
    <div className="space-y-3" data-testid="knowledge-doc-facets">
      {emptyState}

      {groups.map((group) => (
        <div key={group.kind} className="space-y-1.5">
          <Text size="xs" color="muted">
            {knowledgeFacetKindLabel(group.kind)}
          </Text>
          <div className="flex flex-wrap items-center gap-1.5">
            {group.pills.map((pill, index) =>
              editingToken === pill.facet.assignmentToken ? (
                <div
                  key={pill.facet.assignmentToken}
                  className="flex w-full items-center gap-2"
                >
                  <Input
                    value={editValue}
                    onChange={(event) => setEditValue(event.target.value)}
                    className="h-8 max-w-[12rem] text-sm"
                    data-testid={`knowledge-doc-${group.kind}-correct-input-${index}`}
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={busy}
                    onClick={() => saveEdit(pill.facet)}
                    data-testid={`knowledge-doc-${group.kind}-correct-save-${index}`}
                  >
                    {t('library.save')}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => setEditingToken(null)}
                    data-testid={`knowledge-doc-${group.kind}-correct-cancel-${index}`}
                  >
                    {t('library.cancel')}
                  </Button>
                </div>
              ) : (
                <span
                  key={pill.facet.assignmentToken}
                  className={`inline-flex items-center gap-1 rounded-full border py-0.5 pl-3 pr-1.5 text-xs ${
                    pill.facet.confirmed
                      ? 'border-card-border'
                      : 'border-chart-warning/40 bg-chart-warning/10'
                  }`}
                  title={
                    facetConfidenceLabel(pill.facet.confidence) ||
                    pill.facet.displayName
                  }
                  data-testid={`knowledge-doc-${group.kind}-pill-${index}`}
                >
                  {linkToLibrary ? (
                    <Link
                      href={`/knowledge?${encodeURIComponent(group.kind.toLowerCase())}=${encodeURIComponent(pill.value)}`}
                      className="hover:underline"
                      data-testid={`knowledge-doc-${group.kind}-pill-link-${index}`}
                    >
                      {pillLabel(group.kind, pill.value)}
                    </Link>
                  ) : (
                    <span>{pillLabel(group.kind, pill.value)}</span>
                  )}
                  {pill.facet.confirmed && !editConfirmed ? null : (
                    <span className="flex items-center gap-0.5">
                      {pill.facet.confirmed ? null : (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-5 w-5"
                          aria-label={t('library.preview.confirmTag', { value: pill.value })}
                          disabled={busy}
                          onClick={() => onConfirmFacet(pill.facet)}
                          data-testid={`knowledge-doc-${group.kind}-confirm-${index}`}
                        >
                          <Check className="h-3 w-3" />
                        </Button>
                      )}
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-5 w-5"
                        aria-label={t('library.preview.rejectTag', { value: pill.value })}
                        disabled={busy}
                        onClick={() => onRejectFacet(pill.facet)}
                        data-testid={`knowledge-doc-${group.kind}-reject-${index}`}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-5 w-5"
                        aria-label={t('library.preview.correctTag', { value: pill.value })}
                        disabled={busy}
                        onClick={() => startEditing(pill.facet, pill.value)}
                        data-testid={`knowledge-doc-${group.kind}-correct-${index}`}
                      >
                        <Pencil className="h-3 w-3" />
                      </Button>
                    </span>
                  )}
                </span>
              ),
            )}
          </div>
        </div>
      ))}

      {ownerName ? (
        <div className="space-y-1.5">
          <Text size="xs" color="muted">
            {t('library.preview.owner')}
          </Text>
          <div className="flex flex-wrap items-center gap-1.5">
            {linkToLibrary ? (
              <Link
                href={`/knowledge?owner=${encodeURIComponent(ownerName)}`}
                className="inline-flex items-center rounded-full border border-card-border px-3 py-0.5 text-xs hover:bg-muted"
                data-testid="knowledge-doc-owner-pill"
              >
                {ownerName}
              </Link>
            ) : (
              <span
                className="inline-flex items-center rounded-full border border-card-border px-3 py-0.5 text-xs"
                data-testid="knowledge-doc-owner-pill"
              >
                {ownerName}
              </span>
            )}
          </div>
        </div>
      ) : null}

      {hasUnconfirmed ? (
        <Button
          type="button"
          size="sm"
          variant="outline"
          shape="pill"
          disabled={busy}
          onClick={onConfirmAllFacets}
          data-testid="knowledge-doc-accept-all"
        >
          {t('library.preview.acceptAll')}
        </Button>
      ) : null}
    </div>
  )
}
